import React, { useEffect, useState } from "react";
import { Menu, X, Sparkles, Crown, Globe } from "lucide-react";
import Logo from "./Logo";
import { NAV } from "@/constants/testIds";
import { useLang } from "@/lib/lang";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  const links = [
    { label: lang === "fr" ? "Personnages" : "Characters", href: "/#personnages", id: "nav-link-personnages" },
    { label: t("nav.barde"), href: "/barde", id: NAV.linkBarde },
    { label: t("nav.druide"), href: "/druide", id: NAV.linkDruide },
    { label: t("nav.taverne"), href: "/pub", id: NAV.linkTaverne },
    { label: t("nav.suantrai"), href: "/suantrai", id: "nav-link-suantrai" },
    { label: lang === "fr" ? "Geantraí · Joie" : "Geantraí · Joy", href: "/geantrai", id: "nav-link-geantrai" },
    { label: lang === "fr" ? "🌿 Éléments" : "🌿 Elements", href: "/elements", id: "nav-link-elements" },
    { label: lang === "fr" ? "Guantraí · Poèmes" : "Guantraí · Poems", href: "/guantrai", id: "nav-link-guantrai" },
    { label: t("nav.vate"), href: "/vate", id: NAV.linkVate },
    { label: t("nav.gaia"), href: "/gaia", id: "nav-link-gaia" },
    { label: "Ahura Mazda", href: "/ahura", id: "nav-link-ahura" },
    { label: "Le Dagda", href: "/dagda", id: "nav-link-dagda" },
    { label: t("nav.farfadet"), href: "/farfadet", id: NAV.linkFarfadet },
    { label: t("nav.korrigan"), href: "/korrigan", id: NAV.linkKorrigan },
    { label: t("nav.grammarien"), href: "/grammarien", id: NAV.linkGrammarien },
    { label: lang === "fr" ? "⚡ Amis des Celtes" : "⚡ Celts' Friends", href: "/amis-des-celtes", id: "nav-link-amis" },
    { label: lang === "fr" ? "⚔️ Débat olympien" : "⚔️ Olympian Debate", href: "/debat-olympien", id: "nav-link-debat" },
    { label: "Zeus", href: "/zeus", id: "nav-link-zeus" },
    { label: "Apollon", href: "/apollon", id: "nav-link-apollon" },
    { label: "Éros", href: "/eros", id: "nav-link-eros" },
    { label: lang === "fr" ? "☀️ Oracle de Delphes" : "☀️ Oracle of Delphi", href: "/oracle-delphe", id: "nav-link-delphe" },
    { label: lang === "fr" ? "✨ Phanès" : "✨ Phanes", href: "/phanes", id: "nav-link-phanes" },
    { label: lang === "fr" ? "Livre d'Urantia" : "Urantia Book", href: "/urantia", id: "nav-link-urantia" },
    { label: lang === "fr" ? "🌍 Polyglotte" : "🌍 Polyglot", href: "/polyglotte", id: "nav-link-polyglotte" },
    { label: lang === "fr" ? "✨ Anges Guides" : "✨ Guiding Angels", href: "/anges-guides", id: "nav-link-anges" },
    { label: t("nav.traditions"), href: "/traditions", id: NAV.linkTraditions },
    { label: t("nav.oracles"), href: "/traditions#oracles", id: NAV.linkOracles },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <nav
          className={`flex items-center justify-between gap-4 rounded-full px-4 sm:px-6 py-3 transition-all duration-500 ${
            scrolled ? "mystic-card" : ""
          }`}
        >
          <a href="/" data-testid={NAV.logo} className="shrink-0">
            <Logo />
          </a>

          <div className="hidden lg:flex items-center gap-2 flex-1 min-w-0">
            <span
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-[0.2em]"
              style={{
                background: "rgba(212, 175, 55, 0.15)",
                border: "1px solid rgba(212, 175, 55, 0.4)",
                color: "var(--gold)",
              }}
              data-testid="nav-menu-badge"
            >
              <Menu size={10} />
              {lang === "fr" ? "Menu" : "Menu"}
            </span>
            <div
              className="flex items-center gap-0 flex-1 overflow-x-auto nav-scroll"
              data-testid="nav-scroll-container"
              style={{
                scrollbarWidth: "thin",
                WebkitOverflowScrolling: "touch",
                maskImage: "linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)",
                paddingBottom: 2,
              }}
            >
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={l.id}
                  className="shrink-0 px-2.5 py-2 text-[12px] hover:text-white transition-colors relative group whitespace-nowrap"
                  style={{ color: "var(--violet-soft)" }}
                >
                  {l.label}
                  <span
                    className="absolute left-2.5 right-2.5 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"
                    style={{ background: "var(--gold)" }}
                  />
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* FR/EN toggle */}
            <div
              className="hidden sm:flex items-center rounded-full text-[11px] font-mono overflow-hidden"
              style={{ background: "rgba(107, 63, 160, 0.25)", border: "1px solid rgba(214, 193, 255, 0.25)" }}
              data-testid="nav-lang-toggle"
            >
              <button
                type="button"
                onClick={() => setLang("fr")}
                data-testid="nav-lang-fr"
                className="px-2.5 py-1.5 transition-colors"
                style={{
                  background: lang === "fr" ? "var(--gold)" : "transparent",
                  color: lang === "fr" ? "var(--void)" : "var(--violet-soft)",
                  fontWeight: lang === "fr" ? 600 : 400,
                }}
                aria-pressed={lang === "fr"}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="nav-lang-en"
                className="px-2.5 py-1.5 transition-colors"
                style={{
                  background: lang === "en" ? "var(--gold)" : "transparent",
                  color: lang === "en" ? "var(--void)" : "var(--violet-soft)",
                  fontWeight: lang === "en" ? 600 : 400,
                }}
                aria-pressed={lang === "en"}
              >
                EN
              </button>
            </div>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={NAV.mobileToggle}
              className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(107, 63, 160, 0.25)",
                color: "var(--violet-soft)",
                border: "1px solid rgba(214, 193, 255, 0.2)",
              }}
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>

        <div className={`lg:hidden overflow-hidden transition-all duration-500 ${open ? "max-h-[600px] mt-2 opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="mystic-card rounded-2xl p-4 flex flex-col max-h-[70vh] overflow-y-auto">
            <div className="flex gap-2 px-4 pb-3 border-b border-white/5" data-testid="nav-mobile-lang">
              {["fr", "en"].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)} data-testid={`nav-mobile-lang-${l}`}
                        className="px-3 py-1.5 rounded-full text-[11px] font-mono uppercase"
                        style={{ background: lang === l ? "var(--gold)" : "rgba(107,63,160,0.25)", color: lang === l ? "var(--void)" : "var(--violet-soft)" }}>
                  {l}
                </button>
              ))}
            </div>
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-4 py-3 text-sm border-b border-white/5 last:border-b-0"
                style={{ color: "var(--violet-soft)" }}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
